import { ColDef } from 'ag-grid-community';

const BASE_CMS_URL = '/v2/contactManagement';

export const TAB_VALUES = {
    COMPANY_TAB_VALUE: BASE_CMS_URL,
    CONTACT_TAB_VALUE: `${BASE_CMS_URL}/contacts`
};

export const TAB_OPTIONS = [
    { label: 'Companies', value: TAB_VALUES.COMPANY_TAB_VALUE },
    { label: 'Contacts', value: TAB_VALUES.CONTACT_TAB_VALUE }
];

export const BUSINESS_TYPE_OPTIONS = [
    { label: 'Architect', value: 'Architect' },
    { label: 'Contractor', value: 'Contractor' },
    { label: 'Distributor', value: 'Distributor' },
    { label: 'Electrical Contractor', value: 'Electrical Contractor' },
    { label: 'Engineer', value: 'Engineer' },
    { label: 'General Contractor', value: 'General Contractor' },
    { label: 'Landscape Architect', value: 'Landscape Architect' },
    { label: 'Lighting Designer', value: 'Lighting Designer' },
    { label: 'Municipality', value: 'Municipality' },
    { label: 'National Account', value: 'National Account' },
    { label: 'Owner', value: 'Owner' },
    { label: 'Sales Agency', value: 'Sales Agency' }
];

export const defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    suppressFillHandle: true,
    suppressMenu: true,
    sortable: true,
    unSortIcon: true,
    menuTabs: ['filterMenuTab'],
    comparator: (valueA: string, valueB: string) => {
        return valueA?.toLowerCase().localeCompare(valueB?.toLowerCase());
    }
};

export const DELETE_COMPANY_TITLE = 'Delete Company(ies) & Associated Contact(s)?';

export const VISIBILITY_RADIO_VALUE = {
    ALL_BRANCH_LOCATIONS: 'all_branch_locations',
    SPECIFIC_BRANCH_LOCATIONS: 'specific_branch_locations'
} as const;

export const VISIBILITY_RADIO_OPTIONS = [
    { label: 'All Locations', value: VISIBILITY_RADIO_VALUE.ALL_BRANCH_LOCATIONS },
    { label: 'Specific Branch Locations', value: VISIBILITY_RADIO_VALUE.SPECIFIC_BRANCH_LOCATIONS }
];

export const acceptedFileTypes = ['.png', '.jpg'];

export const acceptedFileTypesString = 'Supported file types: PNG, JPG';

export const COMPANY_DETAIL_PAGE_DESCRIPTION =
    "The company details, including the logo, are used to pre-populate the 'Addressed To' section of your documents based on the selected primary customer for the bill of materials.\nYou can always edit the information during the document creation process.";

/**
 * List of columns to add in csv field. New fields can be added or removed in the object
 * It will reflect in the downloaded csv format.
 * NOTE: Please do not break the description in multiple lines. If needed, use \n escape character as already used.
 * The "" and /n inside the description of the csv fileds are intended.
 * 1. "" Helps in adding multiple lines in single cell in csv
 * w. /n helps in adding new lines in single cell of csv. Which is requirement.
 */

export const UPLOAD_CONTACTS_CSV_FIELDS = [
    {
        label: 'Contact - First Name',
        description: '"First name of the contact this line item represents.\nThis is a required field. (if missing, only the company object will be created)"'
    },
    {
        label: 'Contact - Last Name',
        description: '"Last name of the contact this line item represents.\nThis is a required field. (if missing, only the company object will be created)"'
    },
    {
        label: 'Contact - CRM ID',
        description: 'CRM ID of the contact this line item represents.'
    },
    {
        label: 'Contact - Job Title',
        description: 'Job title of the contact this line item represents.'
    },
    {
        label: 'Contact - Email',
        description: 'Email address of the contact this line item represents.'
    },
    {
        label: 'Contact - Phone Number',
        description: 'Phone number of the contact this line item represents.'
    },
    {
        label: 'Contact - Notes',
        description: 'Any notes associated with the contact this line item represents.'
    },
    {
        label: 'Contact - Outside Sales Name',
        description: '"First_Name and Last_Name of the outside Sales (internal to your company) associated with the contact this line item represents.\nAccepted entries: Valid Parspec user."'
    },
    {
        label: 'Contact - Outside Sales ERP ID',
        description: 'ERP ID of the outside Sales (internal to your company) associated with the contact this line item represents.'
    },
    {
        label: 'Company - Company Name',
        description: '"Name of the company of the contact this line item represents.\nThis is a required field."'
    },
    {
        label: 'Company - DUNS #',
        description: 'DUNS number of the company of the contact this line item represents.'
    },
    {
        label: 'Company - CRM ID',
        description: 'CRM ID of the company of the contact this line item represents.'
    },
    {
        label: 'Company - ERP ID',
        description: 'ERP ID of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Business Type',
        description: `"Business type of the company of the contact this line item represents.\nAccepted Entries: 'Architect'; 'Contractor'; 'Distributor'; 'Electrical Contractor'; 'Engineer'; 'General Contractor'; 'Landscape Architect'; 'Lighting Designer'; 'Municipality'; 'National Account'; 'Owner'; 'Sales Agency'."`
    },
    {
        label: 'Company - Email',
        description: 'Email address of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Phone Number',
        description: 'Phone number of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Website',
        description: 'Website address of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Address Line 1',
        description: 'Address line 1 of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Address Line 2',
        description: 'Address line 2 of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Country',
        description: `"Country of the company of the contact this line item represents.\nAccepted Entries: 'USA'; 'CANADA'."`
    },
    {
        label: 'Company - City',
        description: 'City of the company of the contact this line item represents.'
    },
    {
        label: 'Company - State',
        description: '"State code of the company of the contact this line item represents.\nAccepted formats: Two letter state abbreviation (ex: CA, NY, etc.)."'
    },
    {
        label: 'Company - Zip',
        description: 'Zip code of the company of the contact this line item represents.'
    },
    {
        label: 'Company - Location Access',
        description: `"Parspec locations that have access to the company of the contact this line item represents.\nAccepted entries: Valid Parspec branch location ID, comma separated.\nLocation ID is the # displayed at the end of the URL when editing a branch location.\nWill default to 'All Locations' if left empty."`
    },
    {
        label: 'Company - Location ERP ID Access',
        description: `"ERP IDs of locations in ERP that have access to the company of the contact this line item represents.\nAccepted entries: Valid location ERP IDs, comma separated.\nWill default to 'All Locations' if left empty.\nThis field works in combination or replacement of 'Company - Location Access'."`
    },
    {
        label: 'Company - Notes',
        description: 'Any notes associated with the company of the contact this line item represents.'
    },
    {
        label: 'Company - Outside Sales Name',
        description: 'First_Name and Last_Name of the outside Sales (internal to your company) associated with the company of the contact this line item represents.'
    },
    {
        label: 'Company - Outside Sales ERP ID',
        description: 'ERP ID of the outside Sales (internal to your company) associated with the company of the contact this line item represents.'
    }
] as const;

const csvTitles = UPLOAD_CONTACTS_CSV_FIELDS.map((value) => value.label).join(',');
const csvHints = UPLOAD_CONTACTS_CSV_FIELDS.map((value) => value.description).join(',');

// This specifies that first row should have titles, second row should have hints, and then few empty rows.
// NOTE: Please keep it in 1 line, to prevent any unnecessary issues of new lines.
export const UPLOAD_CONTACTS_CSV_TEMPLATE = `${csvTitles}\n${csvHints}\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n,,,\n`;

export const CONTACT_NUMBER_REQEX = new RegExp('^[0-9-().]*$');

export const NUMBER_AND_DASHES_REGEX = /^[\d-]+$/;

export const DUNS_NO_SIZE = 9;
// Valid duns no. is either 9 ditit number or number with this format xx-xxx-xxxx
export const DUNS_NUM_ONLY_REGEX = /^\d{9}$/;
export const DUNS_NUM_DASH_REGEX = /^\d{2}-\d{3}-\d{4}$/;
export const COMPANY_SERVER_ERROR_FIELDS = ['crm_id', 'erp_id', 'duns_hashtag'] as const;
export const CONTACT_SERVER_ERROR_FIELDS = ['crm_id'] as const;

export const MAP_CRM_FIELD_TO_ERR_KEY = {
    erp_id: 'erpError',
    crm_id: 'crmError',
    duns_hashtag: 'dunsError'
} as const;

export const CRM_ENTITY_TYPE = {
    COMPANY: 'Company',
    CONTACT: 'Contact'
} as const;

export const LOCAL_TABLE_FIELDS = {
    OUTSIDE_SALES_AGENT_NAME: 'outside_sales_agent_name'
} as const;

export const RESET_SELECT_OPTION = {
    value: '-',
    label: '-'
} as const;

export const SUMMARY_MODAL = {
    DISCARDED_TEXT: 'Some uploaded items will be discarded, as they match multiple existing entries, or existing entries match multiple imported entries for one or more of the following fields:',
    DISCARDED_SUB_TEXT: 'Download and resolve these discrepancies before re-importing.',
    EMPTY_SUMMARY_TEXT: 'All items were identified as duplicates, there will be no change to existing Contact and/or Company records.'
};

export const MERGE_RECORDS_MODAL = {
    TITLE: "Select the primary record whose fields to keep. The other record will be merged to that record, including the projects and BOMs they're associated to."
};
